import { t as globalT, t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Button, Group, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useInsertMutation } from "@supabase-cache-helpers/postgrest-react-query";
import { PostgrestError } from "@supabase/supabase-js";
import { ErrorAlert } from "@sweco-se/reality-frontend";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback } from "react";
import z from "zod";

import { api } from "@/api.ts";

export const Route = createFileRoute("/projects/create")({
  component: RouteComponent,
  validateSearch: z.object({ next: z.string().startsWith("/").optional() }),
  head: () => ({
    meta: [
      {
        title: globalT`Sweco REALity` + " - " + globalT`Create project`,
      },
    ],
  }),
});

function RouteComponent() {
  const { next } = Route.useSearch();

  const form = useForm({
    initialValues: {
      identifier: "",
      name: "",
      // TODO: implement date pickers
      start: "2025-01-01",
      end: "2030-12-31",
    },
    validate: {
      identifier: (value) =>
        /^[A-Z0-9]{8}(-[A-Z0-9]{3})?$/.test(value)
          ? null
          : t`Invalid identifier format`,
      name: (value) => (value.trim().length > 0 ? null : t`Name is required`),
    },
  });

  const { mutateAsync: insert } = useInsertMutation(
    api.from("project"),
    ["id"],
    "identifier",
  );
  const navigate = Route.useNavigate();
  const handleSubmit = useCallback<Parameters<(typeof form)["onSubmit"]>[0]>(
    async (values) => {
      try {
        await insert([values]);
        await navigate({
          to: next ?? "/projects/$identifier",
          params: { identifier: values.identifier },
        });
      } catch (e) {
        if (e instanceof PostgrestError) {
          if (
            e.code === "23505" &&
            new RegExp("Key \\(identifier\\)=\\(.*\\) already exists.").test(
              e.details,
            )
          ) {
            form.setFieldError(
              "identifier",
              t`A project with this identifier already exists.`,
            );
          } else {
            form.setFieldError("__root", e.message);
          }
        } else if (e instanceof Error) {
          form.setFieldError("__root", e.message);
        }
      }
    },
    [form, insert, navigate, next],
  );

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <h1>
          <Trans>Create new project</Trans>
        </h1>
        <TextInput
          label={t`Identifier`}
          placeholder="XXXXXXXX(-XXX)"
          withAsterisk
          disabled={form.submitting}
          key={form.key("identifier")}
          {...form.getInputProps("identifier")}
        />
        <TextInput
          label={t`Name`}
          withAsterisk
          disabled={form.submitting}
          key={form.key("name")}
          {...form.getInputProps("name")}
        />
        <Group justify="flex-end" mt="md">
          <Button
            type="submit"
            loading={form.submitting}
            disabled={form.submitting}
            color="green"
          >
            <Trans>Create</Trans>
          </Button>
        </Group>
        {form.errors.__root && <ErrorAlert>{form.errors.__root}</ErrorAlert>}
      </Stack>
    </form>
  );
}
